import { getFinancialYearLabels } from "../../brsrFinancialYearLabels";
import { SHEET_NAME as LIFE_CYCLE_ASSESSMENT_SHEET } from "./lifeCycleAssessmentSheet";
import { SHEET_NAME as ACTION_TAKEN_SHEET } from "./actionTakenSheet";
import { SHEET_NAME as RECYCLED_OR_REUSED_INPUT_SHEET } from "./recycledOrReusedInputSheet";
import { SHEET_NAME as RECLAIMED_SHEET } from "./reclaimedSheet";

const HEADER_FILL = {
  type: "pattern",
  pattern: "solid",
  fgColor: { argb: "FFB9D1EA" },
};

const SUBHEADER_FILL = {
  type: "pattern",
  pattern: "solid",
  fgColor: { argb: "FFD9D9D9" },
};

const TABLE_HEADER_FILL = {
  type: "pattern",
  pattern: "solid",
  fgColor: { argb: "FFFCE4D6" },
};

const THIN_BLACK = {
  top: { style: "thin", color: { argb: "FF000000" } },
  left: { style: "thin", color: { argb: "FF000000" } },
  bottom: { style: "thin", color: { argb: "FF000000" } },
  right: { style: "thin", color: { argb: "FF000000" } },
};

const BODY_FONT = { name: "Calibri", size: 11 };
const LINK_FONT = {
  name: "Calibri",
  size: 11,
  color: { argb: "FF0563C1" },
  underline: true,
};

const PRINCIPLE_TITLE =
  "Businesses should provide goods and services in a manner that is sustainable and safe.";

const COL_D = 4;
const COL_E = 5;
const COL_F = 6;
const COL_G = 7;
const COL_H = 8;
const COL_I = 9;
const COL_J = 10;
const COL_K = 11;
const COL_L = 12;
const COL_END = COL_L;
const COL_VALUE_START = COL_K;
const COL_VALUE_END = COL_L;
const COL_LABEL_END = COL_VALUE_START - 1;

const MIN_ROW_HEIGHT = 18;
const LINE_HEIGHT = 15;
const COL_WIDTH_FULL_TABLE = 22 + 12 * 9; // columns D through L

function estimateRowHeight(text, colWidth, minHeight = MIN_ROW_HEIGHT) {
  const str = String(text ?? "");
  if (!str.trim()) return minHeight;
  const charsPerLine = Math.max(8, Math.floor(colWidth * 0.85));
  const lineCount = str.split(/\r?\n/).reduce(
    (total, part) => total + Math.max(1, Math.ceil(part.length / charsPerLine)),
    0
  );
  return Math.max(minHeight, lineCount * LINE_HEIGHT + 8);
}

function setAutoRowHeight(ws, row, parts) {
  ws.getRow(row).height = parts.reduce(
    (max, { text, width }) => Math.max(max, estimateRowHeight(text, width)),
    MIN_ROW_HEIGHT
  );
}

function mergedColWidth(startCol, endCol, widths) {
  let total = 0;
  for (let col = startCol; col <= endCol; col++) {
    const key = String.fromCharCode(64 + col);
    total += widths[key] ?? 12;
  }
  return total;
}

const COLUMN_WIDTHS = {
  A: 2, B: 2, C: 2, D: 22, E: 12, F: 12, G: 12, H: 12, I: 12, J: 12, K: 12, L: 12,
};

function borderCell(cell) {
  cell.border = THIN_BLACK;
}

function setCell(ws, row, col, value, options = {}) {
  const cell = ws.getCell(row, col);
  if (options.link) {
    cell.value = { text: value || "View Details", hyperlink: options.link };
    cell.font = LINK_FONT;
  } else {
    cell.value = value;
    cell.font = options.bold ? { ...BODY_FONT, bold: true } : BODY_FONT;
  }
  borderCell(cell);
  cell.alignment = {
    horizontal: options.align ?? (options.link ? "center" : "left"),
    vertical: options.vertical || "middle",
    wrapText: true,
  };
  if (options.fill) cell.fill = options.fill;
}

function mergeRow(ws, row, startCol, endCol, value, options = {}) {
  if (startCol < endCol) ws.mergeCells(row, startCol, row, endCol);
  setCell(ws, row, startCol, value, options);
  for (let col = startCol + 1; col <= endCol; col++) {
    borderCell(ws.getCell(row, col));
  }
}

function safeText(value, fallback = "-") {
  if (value === null || value === undefined || value === "") return fallback;
  if (Array.isArray(value)) return value.length ? value.join(", ") : fallback;
  if (typeof value === "object") return fallback;
  return String(value);
}

function toYesNo(value) {
  if (value === true || value === "true" || value === "yes" || value === "Yes") return "Yes";
  if (value === false || value === "false" || value === "no" || value === "No") return "No";
  return "-";
}

function formatPercent(value) {
  if (value === null || value === undefined || value === "") return "-";
  const str = String(value);
  return str.includes("%") ? str : `${str}%`;
}

function formatNumber(value) {
  if (value === null || value === undefined || value === "") return "-";
  const num = Number(value);
  if (!Number.isNaN(num)) return num.toFixed(2);
  return String(value);
}

function addSectionHeader(ws, row, title, fill = HEADER_FILL) {
  mergeRow(ws, row, COL_D, COL_END, title, { bold: true, align: "center", fill });
  return row + 1;
}

function addQuestionRow(ws, row, label, value, options = {}) {
  if (options.fullWidth || (value === "" && !options.valueCol)) {
    mergeRow(ws, row, COL_D, COL_END, label, { align: "left", vertical: "top", bold: options.bold });
    setAutoRowHeight(ws, row, [{ text: label, width: COL_WIDTH_FULL_TABLE }]);
    return row + 1;
  }
  const valueCol = options.valueCol || COL_E;
  const valueEnd = options.valueEnd || COL_END;
  mergeRow(ws, row, COL_D, valueCol - 1, label, { align: "left", vertical: "top", bold: options.bold });
  mergeRow(ws, row, valueCol, valueEnd, value ?? "", {
    align: options.valueAlign ?? (options.link ? "center" : "left"),
    vertical: "top",
    link: options.link,
  });
  setAutoRowHeight(ws, row, [
    { text: label, width: mergedColWidth(COL_D, valueCol - 1, COLUMN_WIDTHS) },
    { text: value ?? "", width: mergedColWidth(valueCol, valueEnd, COLUMN_WIDTHS) },
  ]);
  return row + 1;
}

function addLeadershipDetailRow(ws, row, label, sheetName) {
  mergeRow(ws, row, COL_D, COL_LABEL_END, label, { align: "left", vertical: "top" });
  mergeRow(ws, row, COL_VALUE_START, COL_VALUE_END, "View Details", {
    align: "center",
    link: `#'${sheetName}'!D3`,
  });
  setAutoRowHeight(ws, row, [
    { text: label, width: mergedColWidth(COL_D, COL_LABEL_END, COLUMN_WIDTHS) },
  ]);
  return row + 1;
}

function hasLcaConducted(leadership) {
  const assessments = leadership?.lifeCycleAssessments?.assessments;
  if (Array.isArray(assessments) && assessments.length > 0) return true;
  return !!leadership?.lifeCycleAssessments?.status;
}

function addRdCapexTable(ws, row, essential, fyCurrent, fyPrevious) {
  row = addQuestionRow(
    ws,
    row,
    "1. Percentage of R&D and capital expenditure (capex) investments in specific technologies to improve the environmental and social impacts of product and processes to total R&D and capex investments made by the entity, respectively.",
    "",
    { fullWidth: true }
  );

  setCell(ws, row, COL_D, "", { bold: true, fill: TABLE_HEADER_FILL, align: "center" });
  mergeRow(ws, row, COL_E, COL_F, fyCurrent, { bold: true, fill: TABLE_HEADER_FILL, align: "center" });
  mergeRow(ws, row, COL_G, COL_H, fyPrevious, { bold: true, fill: TABLE_HEADER_FILL, align: "center" });
  mergeRow(
    ws,
    row,
    COL_I,
    COL_END,
    "Details of improvements in environmental and social impacts",
    { bold: true, fill: TABLE_HEADER_FILL, align: "center" }
  );
  setAutoRowHeight(ws, row, [
    { text: "Details of improvements in environmental and social impacts", width: mergedColWidth(COL_I, COL_END, COLUMN_WIDTHS) },
  ]);
  row += 1;

  const categories = [
    ["R&D", essential?.rAndD],
    ["Capex", essential?.CapexInvestments],
  ];

  categories.forEach(([label, data]) => {
    setCell(ws, row, COL_D, label, { align: "left" });
    mergeRow(ws, row, COL_E, COL_F, formatPercent(data?.currentFinancialYear), { align: "center" });
    mergeRow(ws, row, COL_G, COL_H, formatPercent(data?.previousFinancialYear), { align: "center" });
    mergeRow(ws, row, COL_I, COL_END, safeText(data?.detailsOfImprovements), {
      align: "left",
      vertical: "top",
    });
    row += 1;
  });

  return row;
}

function addReclaimProcessesSection(ws, row, essential) {
  row = addQuestionRow(
    ws,
    row,
    "3. Describe the processes in place to safely reclaim your products for reusing, recycling and disposing at the end of life, for",
    "",
    { fullWidth: true }
  );

  const items = [
    ["(a) Plastics (including packaging)", essential?.productReclaimProcesses?.plastics],
    ["(b) E-waste", essential?.productReclaimProcesses?.eWaste],
    ["(c) Hazardous waste", essential?.productReclaimProcesses?.hazardousWaste],
    ["(d) other waste", essential?.productReclaimProcesses?.otherWaste],
  ];

  items.forEach(([label, text]) => {
    setCell(ws, row, COL_D, label, { align: "left", vertical: "top" });
    mergeRow(ws, row, COL_E, COL_END, safeText(text), { align: "left", vertical: "top" });
    row += 1;
  });

  return row;
}

function addReclaimedPackagingTable(ws, row, leadership, fyCurrent, fyPrevious) {
  row = addQuestionRow(
    ws,
    row,
    "4. Of the products and packaging reclaimed at end of life of products, amount (in metric tonnes) reused, recycled, and safely disposed:",
    "",
    { fullWidth: true }
  );

  mergeRow(ws, row, COL_D, COL_D, "Category", { bold: true, fill: TABLE_HEADER_FILL, align: "center" });
  mergeRow(ws, row, COL_E, COL_G, fyCurrent, { bold: true, fill: TABLE_HEADER_FILL, align: "center" });
  mergeRow(ws, row, COL_H, COL_J, fyPrevious, { bold: true, fill: TABLE_HEADER_FILL, align: "center" });
  row += 1;

  setCell(ws, row, COL_D, "", { fill: TABLE_HEADER_FILL });
  setCell(ws, row, COL_E, "Re-Used", { bold: true, fill: TABLE_HEADER_FILL, align: "center" });
  setCell(ws, row, COL_F, "Recycled", { bold: true, fill: TABLE_HEADER_FILL, align: "center" });
  setCell(ws, row, COL_G, "Safely Disposed", { bold: true, fill: TABLE_HEADER_FILL, align: "center" });
  setCell(ws, row, COL_H, "Re-Used", { bold: true, fill: TABLE_HEADER_FILL, align: "center" });
  setCell(ws, row, COL_I, "Recycled", { bold: true, fill: TABLE_HEADER_FILL, align: "center" });
  setCell(ws, row, COL_J, "Safely Disposed", { bold: true, fill: TABLE_HEADER_FILL, align: "center" });
  row += 1;

  const packaging = leadership?.reclaimedProductsAndPackaging || {};
  const categories = [
    ["Plastics (include packaging)", "plastics"],
    ["E-Waste", "eWaste"],
    ["Hazardous Waste", "hazardousWaste"],
  ];

  categories.forEach(([label, key]) => {
    const current = packaging?.currentFinancialYear?.[key] || {};
    const previous = packaging?.previousFinancialYear?.[key] || {};
    setCell(ws, row, COL_D, label, { align: "left" });
    setCell(ws, row, COL_E, formatNumber(current?.reUsed), { align: "center" });
    setCell(ws, row, COL_F, formatNumber(current?.recycled), { align: "center" });
    setCell(ws, row, COL_G, formatNumber(current?.safelyDisposed), { align: "center" });
    setCell(ws, row, COL_H, formatNumber(previous?.reUsed), { align: "center" });
    setCell(ws, row, COL_I, formatNumber(previous?.recycled), { align: "center" });
    setCell(ws, row, COL_J, formatNumber(previous?.safelyDisposed), { align: "center" });
    row += 1;
  });

  return row;
}

export function buildPrinciple2Sheet(workbook, sectionC, reportingYear = "") {
  const ws = workbook.addWorksheet("PRINCIPLE 2", { views: [{ showGridLines: true }] });

  const widths = COLUMN_WIDTHS;
  Object.entries(widths).forEach(([col, width]) => {
    ws.getColumn(col).width = width;
  });

  const principle = sectionC?.principle2 || {};
  const essential = principle?.essentialIndicators || {};
  const leadership = principle?.leadershipIndicator || {};
  const fyLabels = getFinancialYearLabels(reportingYear);
  const fyCurrent = fyLabels.currentYear ? `FY (${fyLabels.currentYear})` : "FY (Current Financial Year)";
  const fyPrevious = fyLabels.previousYear ? `PY (${fyLabels.previousYear})` : "PY (Previous Financial Year)";

  let row = 3;
  row = addSectionHeader(ws, row, `PRINCIPLE 2 ${PRINCIPLE_TITLE}`);
  row = addSectionHeader(ws, row, "Essential Indicators", SUBHEADER_FILL);

  row = addRdCapexTable(ws, row, essential, fyCurrent, fyPrevious);

  row = addQuestionRow(
    ws,
    row,
    "2. a. Does the entity have procedures in place for sustainable sourcing? (Yes/No)",
    toYesNo(essential?.sustainableSourcing?.status),
    { valueCol: COL_E, valueEnd: COL_END, valueAlign: "center" }
  );

  if (essential?.sustainableSourcing?.status) {
    row = addQuestionRow(
      ws,
      row,
      "2. b. If yes, what percentage of inputs were sourced sustainably?",
      formatPercent(essential?.sustainableSourcing?.remark),
      { valueCol: COL_E, valueEnd: COL_END, valueAlign: "center" }
    );
  }

  row = addReclaimProcessesSection(ws, row, essential);

  row = addQuestionRow(
    ws,
    row,
    "4. a. Whether Extended Producer Responsibility (EPR) is applicable to the entity's activities (Yes / No)",
    toYesNo(essential?.eprApplicable),
    { valueCol: COL_E, valueEnd: COL_END, valueAlign: "center" }
  );

  if (essential?.eprApplicable) {
    row = addQuestionRow(
      ws,
      row,
      "4. b. If yes, whether the waste collection plan is in line with the Extended Producer Responsibility (EPR) plan submitted to Pollution Control Boards? (Yes/No)",
      toYesNo(essential?.eprWasteCollectionPlan?.status ?? essential?.eprWasteCollectionPlan?.remark),
      { valueCol: COL_E, valueEnd: COL_END, valueAlign: "left" }
    );
  }

  row = addSectionHeader(ws, row, "Leadership Indicators", SUBHEADER_FILL);

  row = addQuestionRow(
    ws,
    row,
    "1. Has the entity conducted Life Cycle Perspective / Assessments (LCA) for any of its products (for manufacturing industry) or for its services (for service industry)?",
    toYesNo(hasLcaConducted(leadership)),
    { valueCol: COL_VALUE_START, valueEnd: COL_VALUE_END, valueAlign: "center" }
  );
  row = addLeadershipDetailRow(ws, row, "If yes, provide details", LIFE_CYCLE_ASSESSMENT_SHEET);

  row = addLeadershipDetailRow(
    ws,
    row,
    "2. If there are any significant social or environmental concerns and/or risks arising from production or disposal of your products / services, as identified in the Life Cycle Perspective / Assessments (LCA) or through any other means, briefly describe the same along-with action taken to mitigate the same.",
    ACTION_TAKEN_SHEET
  );

  row = addLeadershipDetailRow(
    ws,
    row,
    "3. Percentage of recycled or reused input material to total material (by value) used in production (for manufacturing industry) or providing services (for service industry).",
    RECYCLED_OR_REUSED_INPUT_SHEET
  );

  row = addReclaimedPackagingTable(ws, row, leadership, fyCurrent, fyPrevious);

  row = addLeadershipDetailRow(
    ws,
    row,
    "5. Reclaimed products and their packaging materials (as percentage of products sold) for each product category.",
    RECLAIMED_SHEET
  );

  return ws;
}
